import React, {useEffect, useState} from 'react';
import {Alert, Button, Modal} from 'react-bootstrap';
import Pencil from '@mui/icons-material/EditRounded';
import Plus from '@mui/icons-material/AddRounded';
import {useData} from '../../context/ConfigDataContext';
import {deviceTemplates} from '../../constants/jsonTemplates';
import {deviceRegistry, getDeviceEntry} from '../../constants/deviceRegistry';
import {cleanModbusMeter} from './components/ModbusMeter';
import '../../styles/config.css';

const DeviceDefinition = ({sgrcontainer}) => {
  const {currentJsonText, updateCurrentJsonText, validateCurrentJson} = useData();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeviceTypeModal, setShowDeviceTypeModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedDeviceType, setSelectedDeviceType] = useState(null);
  const [selectedIndexOfDeviceType, setSelectedIndexOfDeviceType] = useState(null);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleChange = (event) => {
    const {name, value, type} = event.target;
    const parsedValue = type === 'number' ? Number(value) : value;
    setSelectedDevice({...selectedDevice, [name]: parsedValue});
  };

  const updateData = () => {
    const newJson = JSON.parse(currentJsonText);
    const deviceToSave = selectedDeviceType === 'ModbusMeter'
      ? cleanModbusMeter(selectedDevice)
      : selectedDevice;
    if (selectedIndexOfDeviceType === null) {
      if (!newJson.devices[selectedDeviceType]) newJson.devices[selectedDeviceType] = [];
      newJson.devices[selectedDeviceType].push(deviceToSave);
    } else {
      newJson.devices[selectedDeviceType][selectedIndexOfDeviceType] = deviceToSave;
    }
    updateCurrentJsonText(JSON.stringify(newJson, null, 2));
    closeModal();
  };

  const deleteDevice = () => {
    if (currentJsonText.match(new RegExp(selectedDevice.name, 'g')).length > 1) {
      setShowConfirm(true);
      closeModal();
      return;
    }
    const newJson = JSON.parse(currentJsonText);
    newJson.devices[selectedDeviceType].splice(selectedIndexOfDeviceType, 1);
    updateCurrentJsonText(JSON.stringify(newJson, null, 2));
    closeModal();
  };

  const editDevice = (type, index, device) => {
    setSelectedDeviceType(type);
    setSelectedIndexOfDeviceType(index);
    setSelectedDevice(device);
    setShowEditModal(true);
  };

  const closeModal = () => {
    setShowEditModal(false);
    setSelectedDeviceType(null);
    setSelectedIndexOfDeviceType(null);
  };

  const addDeviceTypeFromModal = (type) => {
    setSelectedDeviceType(type);
    setSelectedDevice({...deviceTemplates[type]});
    setSelectedIndexOfDeviceType(null);
    setShowEditModal(true);
    setShowDeviceTypeModal(false);
  };

  useEffect(() => {
    console.log(`selected device: ${JSON.stringify(selectedDevice)}`);
  }, [selectedDevice]);

  // ─── Feature-flag filtering ────────────────────────────────────────────────

  const featureFlags = {sgrcontainer};
  const visibleRegistry = deviceRegistry.filter(
    (entry) => !entry.featureFlag || featureFlags[entry.featureFlag],
  );

  // ─── Registry-driven card ─────────────────────────────────────────────────

  const getCard = ({index, type, device}) => {
    const entry = getDeviceEntry(type);
    if (!entry) return null;

    // SmartPi or other non-editable devices
    if (!entry.FormComponent) {
      return (
        <div key={`${type}-${index}`} className="p-2">
          <div className="card device-card island h-100 w-100">
            <div className="card-body text-center">
              <h5>{entry.cardTitle || entry.label}</h5>
              {entry.cardDescription && <p>{entry.cardDescription}</p>}
            </div>
          </div>
        </div>
      );
    }

    const CardComponent = entry.CardComponent;
    return (
      <div key={`${type}-${index}`} className="p-2">
        <div className="card device-card island h-100 w-100">
          <div className="card-body text-center">
            <h5 className={`device-definition${entry.cardLogo ? ' device-definition-sgr' : ''}`}>
              {entry.cardTitle || entry.label}
              {entry.cardLogo && <img src={entry.cardLogo} alt=""/>}
              <button className="btn-link" onClick={() => editDevice(type, index, device)}><Pencil/></button>
            </h5>
            {entry.cardDescription && <p>{entry.cardDescription}</p>}
            {CardComponent && <CardComponent device={device}/>}
          </div>
        </div>
      </div>
    );
  };

  // ─── Registry-driven card list ────────────────────────────────────────────

  const deviceCards = () => {
    const devices = JSON.parse(currentJsonText)?.devices;
    return (
      <div className="device-grid">
        {deviceRegistry.map((entry) => {
          const deviceData = devices?.[entry.type];
          if (!deviceData) return null;
          if (!entry.isArray) {
            return getCard({index: 0, type: entry.type, device: null});
          }
          return deviceData.map((device, index) =>
            getCard({index, type: entry.type, device}),
          );
        })}
        <div key="add-device" className="device-card h-100 w-100" onClick={() => setShowDeviceTypeModal(true)}
             style={{cursor: 'pointer'}}>
          <div className="add-new-card h-100 text-center d-flex align-items-center justify-content-center">
            <div className="card-body d-flex flex-column align-items-center justify-content-center">
              <Plus style={{fontSize: 64}} color="gray" className="mb-3 text-primary"/>
              <h5 className="card-title">Add New Device</h5>
              <p className="card-text">Click to add a new device</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─── Registry-driven modal form ───────────────────────────────────────────

  const renderEditForm = () => {
    const entry = getDeviceEntry(selectedDeviceType);
    if (!entry || !entry.FormComponent) return null;
    const FormComponent = entry.FormComponent;
    if (entry.useSetDevice) {
      return <FormComponent device={selectedDevice} setDevice={setSelectedDevice} setSaveDisabled={setSaveDisabled}/>;
    }
    return <FormComponent device={selectedDevice} handleChange={handleChange} setSaveDisabled={setSaveDisabled}/>;
  };

  if (!validateCurrentJson()) return <Alert variant="danger">Invalid JSON</Alert>;

  return JSON.parse(currentJsonText)?.devices ? (
    <>
      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Device</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderEditForm()}
        </Modal.Body>
        <Modal.Footer>
          {selectedIndexOfDeviceType !== null &&
            <Button variant="warning" onClick={deleteDevice}>Delete</Button>}
          <Button variant="secondary" onClick={closeModal}>Cancel</Button>
          <Button variant="primary" onClick={updateData} disabled={saveDisabled}>Save</Button>
        </Modal.Footer>
      </Modal>
      {/* Confirmation Modal */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Resolve dependencies</Modal.Title>
        </Modal.Header>
        <Modal.Body>Other components depend on this device. Resolve these dependencies first.</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowConfirm(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
      {/* Device Type Selection Modal — registry-driven */}
      <Modal show={showDeviceTypeModal} onHide={() => setShowDeviceTypeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Select Device Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-grid gap-2">
            {visibleRegistry
              .filter((entry) => entry.FormComponent && !entry.hidden) // only addable, non-hidden types
              .map((entry) => (
                <Button
                  key={entry.type}
                  variant="outline-primary"
                  onClick={() => addDeviceTypeFromModal(entry.type)}
                >
                  {entry.label}
                </Button>
              ))}
          </div>
        </Modal.Body>
      </Modal>
      {deviceCards()}
    </>
  ) : null;
};

export default DeviceDefinition;
